// frontend/src/pages/reports/SalesForecastAccuracyReport.jsx
// Report: what was forecast against what was ordered, over the period the
// merchant picks. The server computes every figure over that period, says which
// periods this store has the history to answer, and picks the default
// (reporting/report_periods.py).

import React, { useState, useMemo, useCallback, useRef } from 'react';
import '../../styles/staff-columns.css';
import { useTranslation } from 'react-i18next';
import { AgGridReact } from 'ag-grid-react';
import PageBanner from '../../components/common/PageBanner';
import Tile from '../../components/layout/Tile';
import ReportKpiStrip from '../../components/reports/ReportKpiStrip';
import { ItemFilterBar, useItemFilter } from '../../features/item-filter';
import { useSsrmGrid, buildExportQuery } from '../../hooks/useSsrmGrid';
import { translateRequestError } from '../../queries/api';
import { useAuth } from '../../contexts/AuthContext';
import { useIsShopify, itemIdentityColumns } from '../../components/grid/productIdentity';
import { useAgGridTheme } from '../../utils/agGridTheme';
import { useColumnTypes } from '../../hooks/useColumnTypes';
import { useFormatters } from '../../hooks/useFormatters';
import { AUTO_SIZE_TO_FILL, defaultColDef, defaultGridProps, copyOnlyContextMenuItems, restoreInitialSort } from '../../utils/gridDefaults';
import GridExportButton from '../../components/common/GridExportButton';
import FilterChips from '../../components/common/FilterChips';
import FilterSwitch from '../../components/common/FilterSwitch';
import { buildPinnedTotalsRow, wrapColumnsForSummaryRows } from '../../utils/pinnedTotals';
import { useProductGrouping } from '../../components/grid/productGrouping';
import { useReportFilters } from '../../hooks/useReportFilters';
import '../../styles/pages.css';
import { GridEmptyState } from '../../components/grid/NoRowsOverlay';
import { GridPage, GridViewport } from '../../components/grid/GridPage';

const EXTRA_DEFAULTS = { overrideOnly: false, period: null };

// The column each tile sorts by when pressed; the others keep the report's own
// order.
const KPI_SORT = {
  overall_accuracy: { colId: 'accuracy_pct', sort: 'asc' },
  low_accuracy: { colId: 'accuracy_pct', sort: 'asc' },
  high_accuracy: { colId: 'accuracy_pct', sort: 'desc' },
  large_bias: { colId: 'bias_pct', sort: 'desc' },
};

// Orders an empty shelf held back read as a forecast miss, so the Actual orders
// cell of a row that ran out says so on hover: a variant row how many days, a
// product row how many of its variants. The server reads both for the rows on
// screen only (sales_forecast_accuracy_reporting._out_of_stock_marks); the
// totals row carries neither, and gets no note.
function outOfStockNote(row, t) {
  if (row.days_out_of_stock > 0) {
    return t('reports.salesForecastAccuracy.columns.actualOrdersOutOfStock', { count: row.days_out_of_stock });
  }
  if (row.variants_out_of_stock > 0) {
    return t('reports.salesForecastAccuracy.columns.actualOrdersVariantsOutOfStock', { count: row.variants_out_of_stock });
  }
  return '';
}

function SalesForecastAccuracyReport() {
  const { t } = useTranslation();
  const { userContext } = useAuth();
  const isStaff = userContext?.user?.is_staff ?? false;
  const isShopify = useIsShopify();
  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const { formatNumber, formatDate } = useFormatters();
  const defaultHidden = useMemo(() => ['shopify_product_title'], []);
  // Group variants by product (productGrouping): the switch, the product
  // tree, the row id and the saved column state, one preference for every
  // report. A product row carries the report's totals restricted to that
  // product and the report's own measures over it; only those columns say
  // `sortable: true`, since the server orders the products by them.
  const grouping = useProductGrouping({
    gridId: 'report_sales_forecast_accuracy',
    preferenceKey: 'reports_grouped',
    defaultHidden,
    leafIdFields: ['item_id', 'location_id'],
  });
  const { isGrouped, columns: groupedColumns, variantProductColumn, totalsLabelField } = grouping;
  const gridDefaultColDef = useMemo(
    () => ({ ...defaultColDef, ...grouping.defaultColDef }),
    [grouping.defaultColDef],
  );
  const gridRef = useRef(null);

  const pctFormatter = useCallback(
    (p) => (p.value != null ? `${formatNumber(p.value, 1)}%` : '—'),
    [formatNumber],
  );

  const itemFilter = useItemFilter();
  const { location, setLocation, productCategory, setProductCategory, extras, setExtra } = useReportFilters('sales_forecast_accuracy', EXTRA_DEFAULTS);
  const { overrideOnly, period: pickedPeriod } = extras;

  const [kpiFilter, setKpiFilter] = useState(null);
  const handleKpiClick = useCallback((key) => {
    const newKey = kpiFilter === key ? null : key;
    setKpiFilter(newKey);

    const api = gridRef.current;
    if (!api) return;
    if (KPI_SORT[newKey]) {
      api.applyColumnState({ state: [KPI_SORT[newKey]], defaultState: { sort: null } });
    } else {
      restoreInitialSort(api);
    }
  }, [kpiFilter]);

  const params = useMemo(() => ({
    location_code: location, product_category_id: productCategory?.id || null, ...itemFilter.params,
    period: pickedPeriod ?? undefined,
    override_only: overrideOnly ? 'true' : undefined,
    // overall_accuracy only sorts; the other tiles narrow the rows.
    kpi_filter: kpiFilter && kpiFilter !== 'overall_accuracy' ? kpiFilter : undefined,
  }), [location, productCategory, itemFilter.params, pickedPeriod, overrideOnly, kpiFilter]);

  const [chrome, setChrome] = useState({
    summary: null, totals: null, period: null, periods: null,
  });
  const onData = useCallback((data) => {
    setChrome({
      summary: data.summary ?? null,
      totals: data.totals ?? null,
      period: data.period,
      periods: data.periods,
    });
  }, []);
  const { onGridReady, onGridPreDestroyed, loading, error, pagination } = useSsrmGrid({
    endpoint: '/api/reports/sales-forecast-accuracy/data/',
    params,
    onData,
    gridRef,
  });
  const { summary, totals, periods } = chrome;
  const activePeriod = pickedPeriod ?? chrome.period;

  // The period defines the numbers rather than filtering them, so "all" keeps it.
  const exportProps = {
    endpoint: '/api/reports/sales-forecast-accuracy/export/',
    filename: 'sales_forecast_accuracy',
    getParams: useCallback(
      (scope) => buildExportQuery(
        scope === 'all' ? { period: activePeriod } : { ...params, period: activePeriod },
      ),
      [params, activePeriod],
    ),
  };

  const periodOptions = useMemo(() => (periods ?? []).map((period) => ({
    value: period.key,
    label: t(`reports.salesForecastAccuracy.periods.${period.key}`),
    disabled: !period.available,
    tooltip: period.available
      ? t('reports.salesForecastAccuracy.periods.dateRange', {
        start: formatDate(period.start), end: formatDate(period.end),
      })
      : t('reports.salesForecastAccuracy.periods.notEnoughHistory'),
  })), [periods, t, formatDate]);

  const kpiItems = useMemo(() => {
    if (!summary) return [];
    const measured = summary.measured_items;
    const share = (count) => (measured ? Math.round((count / measured) * 100) : null);
    const bias = summary.overall_bias;
    return [
      {
        label: t('reports.salesForecastAccuracy.kpi.overallAccuracy'),
        value: summary.overall_accuracy != null ? formatNumber(summary.overall_accuracy, 1) : null,
        suffix: '%',
        key: 'overall_accuracy',
        tooltip: t('reports.salesForecastAccuracy.kpi.overallAccuracyTooltip'),
      },
      {
        label: t('reports.salesForecastAccuracy.kpi.lowAccuracy'),
        value: share(summary.low_accuracy_count),
        suffix: '%',
        key: 'low_accuracy',
        tooltip: t('reports.salesForecastAccuracy.kpi.lowAccuracyTooltip'),
      },
      {
        label: t('reports.salesForecastAccuracy.kpi.highAccuracy'),
        value: share(summary.high_accuracy_count),
        suffix: '%',
        key: 'high_accuracy',
        tooltip: t('reports.salesForecastAccuracy.kpi.highAccuracyTooltip'),
      },
      {
        label: t('reports.salesForecastAccuracy.kpi.bias'),
        value: bias != null ? `${bias > 0 ? '+' : ''}${formatNumber(bias, 1)}` : null,
        suffix: '%',
        key: 'large_bias',
        tooltip: t('reports.salesForecastAccuracy.kpi.biasTooltip'),
      },
    ];
  }, [summary, t, formatNumber]);

  const pinnedBottomRowData = useMemo(
    () => buildPinnedTotalsRow(totals, totalsLabelField('item_code'), t('grid.totals.label')),
    [totals, t, totalsLabelField],
  );

  const columnDefs = useMemo(() => wrapColumnsForSummaryRows([
    ...groupedColumns([variantProductColumn()]),
    ...(isGrouped ? [] : itemIdentityColumns(isShopify, {
      productHeader: t('reports.columns.product'),
      codeHeader: t('reports.columns.itemCode'),
      nameHeader: t('reports.columns.itemName'),
    })),
    { field: 'category_label', headerName: t('reports.columns.productCategory'), minWidth: 100 },
    { field: 'location_name', headerName: t('reports.columns.location'), minWidth: 100 },
    { field: 'abc_class', headerName: t('reports.columns.abcClass'), width: 60, type: 'centeredColumn' },
    { field: 'xyz_class', headerName: t('reports.columns.xyzClass'), width: 60, type: 'centeredColumn' },
    { field: 'forecast_qty', sortable: true, headerName: t('reports.salesForecastAccuracy.columns.forecastQty'), type: 'numberColumn', headerTooltip: t('reports.salesForecastAccuracy.columns.forecastQtyTooltip') },
    {
      field: 'actual_orders',
      sortable: true,
      headerName: t('reports.salesForecastAccuracy.columns.actualOrders'),
      type: 'numberColumn',
      headerTooltip: t('reports.salesForecastAccuracy.columns.actualOrdersTooltip'),
      cellClassRules: { 'ag-cell-note-mark': (p) => outOfStockNote(p.data, t) !== '' },
      tooltipValueGetter: (p) => outOfStockNote(p.data, t),
    },
    // The report's own order (sales_forecast_accuracy_reporting._DEFAULT_ORDER).
    { field: 'accuracy_pct', sortable: true, initialSort: 'asc', headerName: t('reports.salesForecastAccuracy.columns.accuracy'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('reports.salesForecastAccuracy.columns.accuracyTooltip') },
    { field: 'mape_pct', sortable: true, headerName: t('reports.salesForecastAccuracy.columns.mape'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('reports.salesForecastAccuracy.columns.mapeTooltip') },
    { field: 'bias_pct', sortable: true, headerName: t('reports.salesForecastAccuracy.columns.bias'), type: 'decimalColumn', valueFormatter: pctFormatter, headerTooltip: t('reports.salesForecastAccuracy.columns.biasTooltip') },
    { field: 'mae', headerName: t('reports.salesForecastAccuracy.columns.mae'), type: 'decimalColumn', headerTooltip: t('reports.salesForecastAccuracy.columns.maeTooltip') },
    { field: 'bias_direction', headerName: t('reports.salesForecastAccuracy.columns.biasDirection'), type: 'centeredColumn', headerClass: 'ag-center-aligned-header', headerTooltip: t('reports.salesForecastAccuracy.columns.biasDirectionTooltip'), valueFormatter: (p) => (p.value ? t(`reports.salesForecastAccuracy.bias.${p.value}`) : '—') },
    { field: 'months_of_history', headerName: t('reports.salesForecastAccuracy.columns.monthsOfHistory'), type: 'numberColumn', headerTooltip: t('reports.salesForecastAccuracy.columns.monthsOfHistoryTooltip') },
    {
      field: 'has_user_override',
      headerName: t('reports.salesForecastAccuracy.columns.userOverride'),
      type: 'centeredColumn',
      headerTooltip: t('reports.salesForecastAccuracy.columns.userOverrideTooltip'),
      valueFormatter: (p) => (p.value ? t('common.yes') : t('common.no')),
    },
    { field: 'variance', sortable: true, headerName: t('reports.salesForecastAccuracy.columns.variance'), type: 'numberColumn', headerTooltip: t('reports.salesForecastAccuracy.columns.varianceTooltip') },
    ...(isStaff ? [
      { field: 'forecast_method', headerName: t('reports.salesForecastAccuracy.columns.forecastMethod'), headerClass: 'staff-column', valueFormatter: (p) => p.value ?? '—' },
    ] : []),
  ]), [isShopify, isGrouped, groupedColumns, variantProductColumn, isStaff, t, pctFormatter]);

  return (
    <div className="page-container">
      <ReportKpiStrip title={t('reports.salesForecastAccuracy.kpi.title')} items={kpiItems} loading={loading} activeKey={kpiFilter} onCardClick={handleKpiClick} storageKey="sales_forecast_accuracy" />
      <PageBanner severity="error" messages={[error && translateRequestError(error, t, 'common.fetchError')]} />
      <div className="tiles-row">
        <Tile
          width="full"
          title={t('nav.reportSalesForecastAccuracy')}
          toolbar={periods && (
            <FilterChips
              options={periodOptions}
              value={activePeriod}
              onChange={(key) => setExtra('period', key)}
            />
          )}
          headerActions={<GridExportButton {...exportProps} />}
        >
          <GridPage>
            <ItemFilterBar
              filter={itemFilter}
              productCategoryFilter={{ selected: productCategory, set: setProductCategory, show: true }}
              locationFilter={{ selected: location, set: setLocation }}
              actions={<>
                {grouping.toggle}
                <FilterSwitch
                  label={t('reports.salesForecastAccuracy.filters.overrideOnly')}
                  checked={overrideOnly}
                  onChange={(on) => setExtra('overrideOnly', on)}
                />
              </>}
            />
            <GridViewport>
              {gridTheme && (
                <GridEmptyState
                  filtersActive={location !== 'all' || !!productCategory || !!itemFilter.filter || !!kpiFilter || !!overrideOnly}
                >
                  <AgGridReact
                    theme={gridTheme}
                    columnDefs={columnDefs}
                    columnTypes={columnTypes}
                    loading={loading}
                    {...defaultGridProps}
                    defaultColDef={gridDefaultColDef}
                    autoSizeStrategy={AUTO_SIZE_TO_FILL}
                    {...grouping.gridProps}
                    rowModelType="serverSide"
                    {...pagination}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    pinnedBottomRowData={pinnedBottomRowData}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>
    </div>
  );
}

export default SalesForecastAccuracyReport;
